import { yupResolver } from '@hookform/resolvers/yup'
import { EFinancingProgram, getECreditApplicationStepList, getERequiredFromList } from '@if/core/types'
import { Paper, Table, TableBody, TableCell, TableContainer, TableRow } from '@mui/material'
import FormControl from '@mui/material/FormControl'
import { SelectValueListItem } from '@src/data/types'
import { memo, useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { ActionsDialog, InputTextField, SelectComponent } from '../../../components'
import { TRootState, useAppSelector } from '../../../data/store'
import { appSelectors } from '../../../data/store/AppStore'
import { DocumentRequired, DocumentRequiredSchema } from '../../../data/types/DocumentSchema'
import { TranslatedEnum } from '../../../data/types/TranslatedEnum'
import { reportErrorToConsole } from '../../../services/error-logger'

interface Props {
  open: boolean
  defaultValue: DocumentRequired | undefined
  onConfirm: (data: DocumentRequired) => void
  onCancel: () => void
}

const DocumentsRequestDialog = ({ open, defaultValue, onConfirm, onCancel }: Props) => {
  const { t } = useTranslation()
  const { financingProgramId } = useParams<{ financingProgramId: EFinancingProgram }>()
  const documentTypes = useAppSelector((state: TRootState) => appSelectors.getDocumentTypes(state, financingProgramId))
  const applicantTypeEnum = useAppSelector(appSelectors.getApplicantTypeEnum)
  const lang = useAppSelector(appSelectors.getCurrentLang) as keyof TranslatedEnum

  const {
    register,
    reset,
    getValues,
    handleSubmit,
    formState: { errors },
  } = useForm<DocumentRequired>({
    mode: 'onBlur',
    defaultValues: defaultValue,
    resolver: yupResolver(DocumentRequiredSchema),
  })

  useEffect(() => {
    reset({
      ...defaultValue,
      financingProgramId,
      requiredFrom: defaultValue?.requiredFrom,
    })
  }, [defaultValue, financingProgramId, reset])

  const creditApplicationStepOptions = useMemo<SelectValueListItem[]>(
    () =>
      getECreditApplicationStepList().map((value) => ({
        label: t(`ECreditApplicationStep.${value}`, { ns: 'enum' }),
        value,
      })),
    [t],
  )
  const requiredFromOptions = useMemo<SelectValueListItem[]>(
    () =>
      getERequiredFromList().map((value) => ({
        label: t(`ERequiredFrom.${value}`, { ns: 'enum' }),
        value,
      })),
    [t],
  )

  return (
    <ActionsDialog
      title={`${t('document.request')} : ${documentTypes[getValues('typeId')][lang]}`}
      open={open}
      onCancel={() => {
        onCancel()
      }}
      onConfirm={handleSubmit(onConfirm, reportErrorToConsole)}
    >
      <TableContainer component={Paper} sx={{ minWidth: '100vh', maxHeight: '75vh', mt: 2 }}>
        <Table>
          <TableBody>
            <TableRow>
              <TableCell align="right">
                <FormControl>
                  <InputTextField
                    disabled
                    label={t('document.docType')}
                    value={defaultValue?.typeId ? documentTypes[defaultValue?.typeId][lang] : null}
                    error={errors.typeId}
                  />
                </FormControl>
              </TableCell>
              <TableCell sx={{ minWidth: '15%' }} align="left">
                <InputTextField
                  disabled
                  label={`${t('common.applicant')}(s)`}
                  value={defaultValue?.applicantType ? applicantTypeEnum[defaultValue?.applicantType][lang] : null}
                  error={errors.applicantType}
                />
              </TableCell>
              <TableCell sx={{ minWidth: '15%' }} align="left">
                <SelectComponent
                  items={creditApplicationStepOptions}
                  {...register('requiredBeforeStep')}
                  error={errors.requiredBeforeStep}
                  label={t('common.requiredFor')}
                />
              </TableCell>
              <TableCell sx={{ minWidth: '15%' }} align="left">
                <SelectComponent
                  items={requiredFromOptions}
                  {...register('requiredFrom')}
                  error={errors.requiredFrom}
                  label={t('common.requiredFrom')}
                />
              </TableCell>
              <TableCell align="left" sx={{ width: '35%' }}>
                <InputTextField label={t('common.comment')} {...register('note')} error={errors.note} />
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </ActionsDialog>
  )
}

export default memo(DocumentsRequestDialog)
